/**
 * productSearchLocal — product search against the device's saved catalog.
 *
 * Moved out of SmartProductSearch 2026-09-21 (owner report: Detailed-mode search
 * took 30–60 s "offline"). The search box asked the SERVER first with no time
 * limit and only fell back to this cache after the request died — on wifi with
 * a dead uplink that is the browser's own give-up time. Quick mode never had
 * the problem because it searches the saved list. Detailed now does the same
 * (cache first), then merges the server's live numbers in when they arrive.
 *
 * Same token + typo rules as Quick mode. The typo pass used to look at the
 * first 200 products only (of ~2,000) — it now covers the whole catalog.
 */
import { getProducts, getInventoryItem, getBranchPrice } from './offlineDB';

// Bounded Levenshtein with early bail-out.
export function levAtMost(a, b, maxDist) {
  if (a === b) return 0;
  const la = a.length, lb = b.length;
  if (Math.abs(la - lb) > maxDist) return maxDist + 1;
  if (la === 0) return lb;
  if (lb === 0) return la;
  let prev = new Array(lb + 1);
  let curr = new Array(lb + 1);
  for (let j = 0; j <= lb; j++) prev[j] = j;
  for (let i = 1; i <= la; i++) {
    curr[0] = i;
    let rowMin = curr[0];
    for (let j = 1; j <= lb; j++) {
      const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
      if (curr[j] < rowMin) rowMin = curr[j];
    }
    if (rowMin > maxDist) return maxDist + 1;
    [prev, curr] = [curr, prev];
  }
  return prev[lb];
}

const SPLIT = /[\s\-/,]+/;
const isShortNumeric = (t) => /^[0-9]{1,3}$/.test(t);
const tokenMatches = (t, haystack, nameWords) =>
  (isShortNumeric(t) ? nameWords.some(w => w.startsWith(t)) : haystack.includes(t));

/** Pure: which products match `query`. → { picks, fuzzyCount } */
export function matchProducts(all, query, { limit = 10 } = {}) {
  const q = (query || '').toLowerCase().trim();
  const tokens = q.split(SPLIT).filter(Boolean);
  if (!q || !tokens.length) return { picks: [], fuzzyCount: 0 };

  const rows = [];
  for (const p of all || []) {
    if (!p || p.active === false) continue;
    const name = (p.name || '').toLowerCase();
    const haystack = `${name} ${(p.sku || '').toLowerCase()} ${(p.barcode || '').toLowerCase()}`;
    rows.push({ p, name, haystack, nameWords: name.split(SPLIT).filter(Boolean) });
  }

  const strict = [];
  for (const r of rows) {
    if (!tokens.every(t => tokenMatches(t, r.haystack, r.nameWords))) continue;
    const rank = r.name.startsWith(q) ? 0 : (r.name.includes(q) ? 1 : 2);
    strict.push({ p: r.p, rank, len: r.name.length });
  }
  if (strict.length) {
    strict.sort((a, b) => a.rank - b.rank || a.len - b.len);
    return { picks: strict.slice(0, limit).map(x => x.p), fuzzyCount: 0 };
  }

  // Typo pass — only when nothing matched exactly
  const fuzzable = tokens.filter(t => t.length >= 4 && !/^[0-9]+$/.test(t));
  if (!fuzzable.length) return { picks: [], fuzzyCount: 0 };
  const exactReq = tokens.filter(t => !fuzzable.includes(t));
  const cap = (t) => (t.length >= 8 ? 2 : 1);
  const fuzzy = [];
  for (const r of rows) {
    if (!exactReq.every(t => tokenMatches(t, r.haystack, r.nameWords))) continue;
    const words = r.haystack.split(SPLIT).filter(Boolean);
    let edits = 0, ok = true;
    for (const t of fuzzable) {
      if (r.haystack.includes(t)) continue;
      const max = cap(t);
      let best = Infinity;
      for (const w of words) {
        if (Math.abs(w.length - t.length) > max) continue;
        const d = levAtMost(t, w, max);
        if (d < best) best = d;
        if (best === 0) break;
      }
      if (best > max) { ok = false; break; }
      edits += best;
    }
    if (ok) fuzzy.push({ p: r.p, edits, len: r.name.length });
  }
  fuzzy.sort((a, b) => a.edits - b.edits || a.len - b.len);
  return { picks: fuzzy.slice(0, limit).map(x => x.p), fuzzyCount: fuzzy.length };
}

/**
 * Pure: fold the server's live rows into the rows already on screen.
 * The cashier may already be arrowing through the list, so rows NEVER move or
 * vanish: a live row replaces its saved twin in place; a product the device had
 * not synced yet is appended at the end.
 */
export function mergeLiveResults(shown, live) {
  const liveById = new Map((live || []).filter(r => r && r.id).map(r => [r.id, r]));
  const merged = (shown || []).map(r => (liveById.has(r.id) ? { ...r, ...liveById.get(r.id), _live: true } : r));
  const have = new Set(merged.map(r => r.id));
  for (const r of liveById.values()) if (!have.has(r.id)) merged.push({ ...r, _live: true });
  return merged;
}

// The saved catalog, kept in memory between keystrokes. The background sync
// rewrites IndexedDB every few minutes, so a short life is enough to pick
// that up without re-reading ~2,000 products on every keystroke.
const CATALOG_TTL_MS = 30 * 1000;
let catalogMemo = { at: 0, rows: null };
export function clearCatalogMemo() { catalogMemo = { at: 0, rows: null }; }

async function savedCatalog(deps) {
  const now = Date.now();
  if (catalogMemo.rows && now - catalogMemo.at < CATALOG_TTL_MS) return catalogMemo.rows;
  const rows = await deps.getProducts();
  if (rows && rows.length) catalogMemo = { at: now, rows };
  return rows || [];
}

const defaultDeps = { getProducts, getInventoryItem, getBranchPrice };

/** Search the saved catalog and dress each hit like a /products/search-detail row. */
export async function searchProductsLocal(query, branchId, deps = defaultDeps) {
  const all = await savedCatalog(deps);
  const { picks, fuzzyCount } = matchProducts(all, query);

  const results = await Promise.all(picks.map(async p => {
    const bp = branchId ? await deps.getBranchPrice(p.id) : null;
    const prices = bp?.prices ? { ...(p.prices || {}), ...bp.prices } : (p.prices || {});
    const cost = bp?.cost_price ?? p.cost_price;
    // A repack's stock lives on its parent sack (the server derives it the same way).
    const derived = !!(p.is_repack && p.parent_id);
    const inv = await deps.getInventoryItem(derived ? p.parent_id : p.id);
    const qty = Number(inv?.quantity ?? 0);
    const perParent = Number(p.units_per_parent) > 0 ? Number(p.units_per_parent) : 1;
    return {
      ...p,
      prices,
      cost_price: cost,
      effective_capital: cost,
      available: derived ? qty * perParent : qty,
      reserved: 0,
      coming: 0,
      ...(derived ? { derived_from_parent: true, parent_stock: qty } : {}),
      branch_set_scheme_keys: bp?.prices ? Object.keys(bp.prices) : [],
    };
  }));

  return { results, fuzzyHint: fuzzyCount > 0 ? { query, count: fuzzyCount } : null };
}
